<template>
  <div class="digital-guide-page" :dir="isHebrew ? 'rtl' : 'ltr'">
    <SingleRideHeader :presentedText="guideInfoLocalized.digitalGuideHeaderText || guideInfoLocalized.heroTitle" />
    <b-container>
      <div class="digital-content after_page_header">
        <!-- Back Button -->
        <div class="digital-back-button-container">
          <router-link :to="backToFeaturedRides" class="digital-back-button">
            <i :class="['fa-solid', isHebrew ? 'fa-arrow-right' : 'fa-arrow-left']"></i>
            {{ guideInfoLocalized.digitalGuideBackToFeaturedRidesText }}
          </router-link>
        </div>
    <!-- Hero Section -->
    <section class="digital-hero">
      <div class="digital-hero-text">
        <h1>{{ guideInfoLocalized.heroTitle }}</h1>
        <p>
          {{ guideInfoLocalized.heroDescription1 }}
        </p>
        <p>
          {{ guideInfoLocalized.heroDescription2 }}
        </p>
      </div>

      <!-- תמונה לדוגמה – להחליף בקובץ אמיתי ללא טקסט בתמונה -->
      <div class="digital-hero-image">
        <img
          :src="guideInfoLocalized.heroImage"
          :alt="guideInfoLocalized.heroImageAlt"
        />
      </div>
    </section>

    <!-- What You Get Section -->
    <section class="digital-section">
      <h2>{{ guideInfoLocalized.whatYouGetTitle }}</h2>

      <div class="digital-grid">
        <div v-for="(card, index) in guideInfoLocalized.cards" :key="index" class="digital-card">
          <h3>{{ card.title }}</h3>
          <p>{{ card.description }}</p>
        </div>
      </div>
    </section>

    <!-- Independent Touring Section -->
    <section class="digital-section">
      <h2>{{ guideInfoLocalized.independentTouringTitle }}</h2>
      <div class="digital-two-columns">
        <div>
          <p>
            {{ guideInfoLocalized.independentTouringDescription1 }}<br>
            {{ guideInfoLocalized.independentTouringDescription2 }}
          </p>
          <ul class="digital-list">
            <li v-for="(item, index) in guideInfoLocalized.independentTouringList" :key="index">
              <span class="digital-checkmark">✓</span>
              <span class="digital-list-text">{{ item }}</span>
            </li>
          </ul>
        </div>

        <!-- תמונה שנייה – שוב, בלי טקסט בתמונה -->
        <div class="digital-inline-image">
          <img
            :src="guideInfoLocalized.independentTouringImage"
            :alt="guideInfoLocalized.independentTouringImageAlt"
          />
        </div>
      </div>
    </section>

    <!-- Time Saving Section -->
    <section class="digital-section">
      <h2>{{ guideInfoLocalized.timeSavingTitle }}</h2>
      <p>
        {{ guideInfoLocalized.timeSavingDescription1 }}
      </p>
      <p>
        {{ guideInfoLocalized.timeSavingDescription2 }}
      </p>
    </section>

    <!-- Author / Ofer Section -->
    <section class="digital-section">
      <h2>{{ guideInfoLocalized.authorTitle }}</h2>
      <div class="digital-two-columns">
        <div>
          <p class="preserve-newlines">
            {{ guideInfoLocalized.authorDescription1 }}
          </p>
          <p>
            {{ guideInfoLocalized.authorDescription2 }}
          </p>
        </div>
        <div class="digital-inline-image">
          <img
            :src="guideInfoLocalized.authorImage"
            :alt="guideInfoLocalized.authorImageAlt"
          />
        </div>
      </div>
    </section>

    <!-- Back Button -->
    <div class="digital-back-button-container">
      <router-link :to="backToFeaturedRides" class="digital-back-button">
        <i :class="['fa-solid', isHebrew ? 'fa-arrow-right' : 'fa-arrow-left']"></i>
        {{ guideInfoLocalized.digitalGuideBackToFeaturedRidesText }}
      </router-link>
    </div>

    <!-- Technical Requirements -->
    <section class="digital-section">
      <h2>{{ guideInfoLocalized.technicalRequirementsTitle }}</h2>
      <ul class="digital-list">
        <li v-for="(item, index) in guideInfoLocalized.technicalRequirementsList" :key="index">
          <span class="digital-checkmark">✓</span>
          <span class="digital-list-text">{{ item }}</span>
        </li>
      </ul>
      <p class="digital-note">
        {{ guideInfoLocalized.technicalNote }}
      </p>
    </section>

    <!-- FAQ Section -->
    <section class="digital-section">
      <h2>{{ guideInfoLocalized.faqTitle }}</h2>

      <div v-for="(faq, index) in guideInfoLocalized.faqItems" :key="index" class="digital-faq-item">
        <h3>{{ faq.question }}</h3>
        <p>{{ faq.answer }}</p>
      </div>
    </section>

    <!-- Back Button -->
    <div class="digital-back-button-container">
      <router-link :to="backToFeaturedRides" class="digital-back-button">
        <i :class="['fa-solid', isHebrew ? 'fa-arrow-right' : 'fa-arrow-left']"></i>
        {{ guideInfoLocalized.digitalGuideBackToFeaturedRidesText }}
      </router-link>
    </div>
      </div>
    </b-container>
    <PageFooter
      v-if="!isSubdomain"
    />
  </div>
</template>

<script>
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import config from "@/common/config";
import { mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: 'DigitalGuideInfo',
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader
  },
  created() {
    this.applyLanguageFromManager();
  },
  watch: {
    '$route.fullPath'() {
      this.applyLanguageFromManager();
    }
  },
  computed: {
    ...mapGetters("theme", ["isSubdomain"]),
    guideInfoLocalized() {
      const managerUrlName = this.getManagerUrlName();
      const guideInfoConfig = config.config.DIGITAL_GUIDE_INFO || {};

      const defaultInfo = guideInfoConfig.default || {};
      const managerInfo = managerUrlName && guideInfoConfig[managerUrlName] ? guideInfoConfig[managerUrlName] : {};

      const lang = (this.getCurrentLanguage || 'he').toLowerCase();
      const defaultLocalized = defaultInfo.localized?.[lang] || {};
      const managerLocalized = managerInfo.localized?.[lang] || {};

      // Important: manager overrides should win over default localized.
      return {
        ...defaultInfo,
        ...defaultLocalized,
        ...managerInfo,
        ...managerLocalized,
        digitalGuideBackToFeaturedRidesText:
          (managerLocalized.digitalGuideBackToFeaturedRidesText ||
            defaultLocalized.digitalGuideBackToFeaturedRidesText ||
            this.t('digitalGuideToRecommendedTrips')),
        heroImageAlt:
          (managerLocalized.heroImageAlt ||
            defaultLocalized.heroImageAlt ||
            this.t('digitalGuideMetaDescription')),
        independentTouringImageAlt:
          (managerLocalized.independentTouringImageAlt ||
            defaultLocalized.independentTouringImageAlt ||
            this.t('digitalGuideNavInterfaceAlt')),
      };
    },
    backToFeaturedRides() {
      // Get manager from route query or params
      const managerUrlName = this.$route.query.manager || this.$route.params.managerUrlName || null;
      
      // If we have a manager, go to their specific featured rides page
      if (managerUrlName) {
        const featuredRides = config.config.FEATURED_RIDES || [];
        const manager = featuredRides.find(m => m.managerUrlName === managerUrlName);
        
        if (manager && manager.slug) {
          return {
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: manager.slug }
          };
        } else if (manager && manager.managerUrlName) {
          return {
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: manager.managerUrlName }
          };
        }
      }
      
      // Default: go to main featured rides page
      return { name: 'featured-rides-landing' };
    }
  },
  methods: {
    getManagerUrlName() {
      let managerUrlName = this.$route.query.manager || this.$route.params.managerUrlName || null;

      if (!managerUrlName) {
        const featuredRides = config.config.FEATURED_RIDES || [];
        const currentPath = this.$route.path;
        if (currentPath.includes('/featured-rides')) {
          const pathParts = currentPath.split('/');
          const pathManager = pathParts[pathParts.length - 1];
          const matchingManager = featuredRides.find(
            (manager) =>
              manager.managerUrlName === pathManager ||
              manager.slug === pathManager ||
              (manager.isDefault && pathManager === 'featured-rides')
          );
          if (matchingManager) {
            managerUrlName = matchingManager.managerUrlName;
          }
        }
      }

      return managerUrlName;
    },
    applyLanguageFromManager() {
      const managerUrlName = this.getManagerUrlName();
      if (!managerUrlName) {
        return;
      }

      const featuredRides = config.config.FEATURED_RIDES || [];
      const manager = featuredRides.find(
        (m) => m.managerUrlName === managerUrlName || m.slug === managerUrlName
      );

      const language = manager?.language;
      if (language) {
        this.$store.dispatch('language/setLanguageFromRide', language);
      }
    }
  },
  metaInfo() {
    const title = this.guideInfoLocalized?.heroTitle || this.t('digitalGuideWhatIsTitle');
    const description = this.guideInfoLocalized?.heroDescription1 || '';
    return {
      title,
      link: [
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' }
      ]
    };
  }
};
</script>

<style scoped>
.digital-guide-page {
  font-family: 'Noto Sans Hebrew', sans-serif;
  background: #f8f9fa;
}

.digital-guide-page[dir="ltr"] {
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
}

.digital-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 6rem;
}

.digital-back-button-container {
  margin-bottom: 3rem;
}

.digital-back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  color: #07A0C3;
  font-size: 1.8rem;
  text-decoration: none;
  padding: 1rem 2rem;
  border: 2px solid #07A0C3;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.digital-back-button:hover {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: #fff;
  border-color: transparent;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.3);
}

.digital-back-button i {
  font-size: 1.4rem;
}

/* Hero */
.digital-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4rem;
  margin-bottom: 6rem;
  background: linear-gradient(135deg, rgba(52, 208, 182, 0.05) 0%, rgba(7, 160, 195, 0.05) 100%);
  padding: 4rem;
  border-radius: 24px;
}

.digital-hero-text {
  flex: 1 1 500px;
}

.digital-hero-text h1 {
  font-size: 4.8rem;
  font-weight: 700;
  margin-bottom: 2.4rem;
  color: #0d2f29;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.digital-guide-page[dir="ltr"] .digital-hero-text {
  text-align: left;
}

.digital-guide-page[dir="ltr"] .digital-hero-text h1,
.digital-guide-page[dir="ltr"] .digital-hero-text p {
  text-align: left;
}

.digital-hero-text p {
  font-size: 2rem;
  line-height: 1.8;
  margin-bottom: 1.6rem;
  color: #495057;
  font-weight: 400;
}

.digital-hero-image {
  flex: 1 1 400px;
  text-align: center;
}

.digital-hero-image img {
  max-width: 100%;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

/* Sections */
.digital-section {
  margin-bottom: 6rem;
  background: #ffffff;
  padding: 4rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.digital-section h2 {
  font-size: 3.6rem;
  font-weight: 700;
  margin-bottom: 3rem;
  color: #0d2f29;
  text-align: center;
  position: relative;
  padding-bottom: 1.6rem;
}

.digital-section h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 50%;
  transform: translateX(50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border-radius: 2px;
}

.digital-section p {
  font-size: 2rem;
  line-height: 1.9;
  margin-bottom: 1.6rem;
  color: #495057;
  font-weight: 400;
}

.digital-guide-page[dir="ltr"] .digital-section p {
  text-align: left;
}

.digital-section p.preserve-newlines {
  white-space: pre-line;
}

/* Grid of cards */
.digital-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.4rem;
  margin-top: 2rem;
}

.digital-card {
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  background: #ffffff;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.digital-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.3s ease;
}

.digital-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(7, 160, 195, 0.15);
  border-color: rgba(7, 160, 195, 0.2);
}

.digital-card:hover::before {
  transform: scaleX(1);
}

.digital-card h3 {
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 1.6rem;
  color: #0d2f29;
  line-height: 1.3;
}

.digital-card p {
  font-size: 1.8rem;
  line-height: 1.8;
  color: #495057;
  margin-bottom: 0;
}

/* Two columns layout */
.digital-two-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 4rem;
  align-items: center;
}

.digital-two-columns > div:first-child {
  flex: 1 1 400px;
}

.digital-two-columns > div:first-child p {
  font-size: 2rem;
  line-height: 1.9;
  margin-bottom: 1.6rem;
}

.digital-guide-page[dir="ltr"] .digital-two-columns > div:first-child {
  text-align: left;
}

.digital-guide-page[dir="ltr"] .digital-two-columns > div:first-child p {
  text-align: left;
}

.digital-inline-image {
  flex: 1 1 400px;
}

.digital-inline-image img {
  max-width: 100%;
  max-height: 385px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

/* List */
.digital-list {
  list-style: none;
  padding: 0;
  margin: 3rem 0 0;
}

.digital-list li {
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  margin-bottom: 1.2rem;
  color: #495057;
}

.digital-checkmark {
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
  margin-top: 0.2rem;
}

.digital-list-text {
  display: block;
  font-size: 2rem;
  line-height: 1.8;
}

.digital-guide-page[dir="ltr"] .digital-list-text {
  text-align: left;
}

.digital-guide-page[dir="ltr"] .digital-card {
  text-align: left;
}

.digital-guide-page[dir="ltr"] .digital-card h3,
.digital-guide-page[dir="ltr"] .digital-card p {
  text-align: left;
}

/* FAQ */
.digital-faq-item {
  margin-bottom: 2.4rem;
  padding: 2.4rem;
  background: rgba(7, 160, 195, 0.03);
  border-radius: 16px;
  border-right: 4px solid #07A0C3;
  transition: all 0.3s ease;
}

.digital-guide-page[dir="ltr"] .digital-faq-item {
  border-right: 0;
  border-left: 4px solid #07A0C3;
  text-align: left;
}

.digital-guide-page[dir="ltr"] .digital-faq-item h3,
.digital-guide-page[dir="ltr"] .digital-faq-item p {
  text-align: left;
}

.digital-faq-item:hover {
  background: rgba(7, 160, 195, 0.06);
  transform: translateX(-4px);
}

.digital-faq-item h3 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
  color: #0d2f29;
}

.digital-faq-item p {
  font-size: 1.9rem;
  line-height: 1.8;
  color: #495057;
  margin-bottom: 0;
}

/* Note */
.digital-note {
  font-size: 1.8rem;
  line-height: 1.8;
  color: #6c757d;
  font-style: italic;
  padding: 2rem;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 12px;
  border-right: 4px solid #ffc107;
  margin-top: 2rem;
}

.digital-guide-page[dir="ltr"] .digital-note {
  border-right: 0;
  border-left: 4px solid #ffc107;
}

/* Responsive */
@media (max-width: 992px) {
  .digital-content {
    padding: 3rem 1.5rem 4rem;
  }

  .digital-hero {
    padding: 3rem;
    gap: 3rem;
  }

  .digital-hero-text h1 {
    font-size: 3.6rem;
  }

  .digital-section {
    padding: 3rem;
  }

  .digital-section h2 {
    font-size: 3rem;
  }

  .digital-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .digital-content {
    padding: 2rem 1rem 3rem;
  }

  .digital-hero {
    flex-direction: column;
    padding: 1.5rem 1rem;
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  .digital-hero-text,
  .digital-hero-image {
    flex: 1 1 auto;
  }

  .digital-hero-text h1 {
    font-size: 2.8rem;
    margin-bottom: 1.6rem;
  }

  .digital-hero-text p {
    font-size: 1.8rem;
  }

  .digital-section {
    padding: 2rem;
    margin-bottom: 4rem;
  }

  .digital-section h2 {
    font-size: 2.4rem;
    margin-bottom: 2rem;
  }

  .digital-section p {
    font-size: 1.8rem;
  }

  .digital-card {
    padding: 2rem;
  }

  .digital-card h3 {
    font-size: 2rem;
  }

  .digital-card p {
    font-size: 1.6rem;
  }

  .digital-two-columns {
    flex-direction: column;
    gap: 2rem;
  }

  .digital-two-columns > div:first-child p {
    font-size: 1.8rem;
  }

  .digital-list li {
    font-size: 1.8rem;
  }

  .digital-faq-item {
    padding: 1.6rem;
  }

  .digital-faq-item h3 {
    font-size: 2rem;
  }

  .digital-faq-item p {
    font-size: 1.7rem;
  }

  .digital-note {
    font-size: 1.6rem;
  }

  .digital-back-button {
    font-size: 1.6rem;
    padding: 0.8rem 1.6rem;
  }
}
</style>

